import { Component, OnDestroy, OnInit } from '@angular/core';
import { HomeBaseComponent } from '../home-base.component';
import { MetaInfoService, MetaName } from '@sdpp-web/lib/portal';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { CompanyService } from './services/company.service';
import { SelectItem } from 'primeng/api';
import { UserService } from './services/user.service';
import { UserFormValidationModel } from './models/user-form-validation-model';
import { Subscription } from 'rxjs';
import { SiteTextService } from './services/siteText.service';
import { SiteText } from './models/siteText';

@Component({
    selector: 's-home-etf',
    templateUrl: './etf.component.html',
    styleUrls: ['./etf.component.scss'],
    standalone: false
})
export class EtfComponent extends HomeBaseComponent implements OnDestroy, OnInit {
  public userFormValidationModel: UserFormValidationModel;
  public showRegisterModal = false;
  public showSuccessModal = false;
  public registerForm: FormGroup;
  public readonly titles = [
    { label: 'Mr', value: 'Mr' },
    { label: 'Mx', value: 'Mx' },
    { label: 'Mrs', value: 'Mrs' },
    { label: 'Miss', value: 'Miss' }
  ];

  public companies: SelectItem[] = [];
  public errorMessage: string | null = null;
  public deployUrl: string;
  public siteTextData: Record<string, SiteText> = {};
  public popUpTitle: string;
  public popUpContent: SafeHtml;
  public showPopUp = false;

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly fb: FormBuilder,
    titleService: Title,
    metaService: MetaInfoService,
    private readonly userService: UserService,
    private readonly companyService: CompanyService,
    private readonly siteTextService: SiteTextService,
    private readonly sanitizer: DomSanitizer
  ) {
    super(metaService);
    titleService.setTitle('ETF Nav Portal');
    this.deployUrl = metaService.getMeta(MetaName.DeployUrl);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.userFormValidationModel = new UserFormValidationModel();
    this.registerForm = this.fb.group({
      title: [null, Validators.required],
      firstName: [null, [Validators.required, Validators.pattern(/^[a-zA-Z]+(\s*[a-zA-Z]*)*$/)]],
      lastName: [null, [Validators.required, Validators.pattern(/^[a-zA-Z]+(\s*[a-zA-Z]*)*$/)]],
      email: [null, [Validators.required, Validators.pattern(/^\s*([\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7})\s*$/)]],
      company: [null, Validators.required],
      emailGroup: [null, [Validators.required, this.emailGroupValidator()]],
      phoneNumber: [
        null,
        [
          Validators.required,
          Validators.pattern(/^\+?(\d{1,3})?[-.\s]?\(?(\d{1,4})\)?[-.\s]?(\d{1,4})[-.\s]?(\d{1,4})[-.\s]?(\d{1,9})$/)
        ]
      ]
    });

    this.loadCompanies();

    const siteCodes = ['NAV_TERMS_OF_USE', 'NAV_DISCLAIMER', 'NAV_CONTACT_US'];
    const subscriptions = siteCodes.map(siteCode => {
      return this.siteTextService.getSiteText(siteCode).subscribe({
        next: (data: SiteText) => {
          this.siteTextData[siteCode] = data;
        }
      });
    });
    this.subscriptions.add(...subscriptions);
  }

  public openRegisterModal() {
    this.showRegisterModal = true;
    this.registerForm.reset();
    this.registerForm.markAsUntouched();
  }

  public closeRegisterModal() {
    this.showRegisterModal = false;
    this.registerForm.reset();
    this.registerForm.get('phoneNumber').setValue('');
    this.errorMessage = null;
  }

  public closeSuccessModal() {
    this.showSuccessModal = false;
  }

  public onSubmit() {
    if (this.registerForm.valid) {
      const { title, firstName, lastName, email, emailGroup, phoneNumber, company } = this.registerForm.value;
      const userData = {
        title,
        firstname: firstName.trim(),
        lastname: lastName.trim(),
        email,
        emailGroup,
        phoneNumber,
        companyId: company
      };

      const subscription = this.userService.saveUser(userData).subscribe({
        next: response => {
          if (response.success) {
            this.showSuccessModal = true;
            this.closeRegisterModal();
            this.errorMessage = null;
          } else {
            this.errorMessage = response.message;
          }
        },
        error: () => {
          this.errorMessage = 'An error occurred while saving the user.';
        }
      });
      this.subscriptions.add(subscription);
    } else {
      this.registerForm.markAllAsTouched();
    }
  }

  public openPopUp(siteCode: string): void {
    if (this.siteTextData[siteCode]) {
      this.popUpContent = this.sanitizer.bypassSecurityTrustHtml(this.siteTextData[siteCode].label);
      this.popUpTitle = this.siteTextData[siteCode].textDisplay;
      this.showPopUp = true;
    }
  }

  public closePopUp(): void {
    this.showPopUp = false;
  }

  private emailGroupValidator(): ValidatorFn {
    return (control: AbstractControl): { invalidEmailGroup: boolean } | null => {
      const emails = control.value ? control.value.split(',').map((email: string) => email.trim()) : [];
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      for (const email of emails) {
        if (!emailPattern.test(email)) {
          return { invalidEmailGroup: true };
        }
      }
    };
  }

  private loadCompanies() {
    const subscription = this.companyService.getCompanies().subscribe({
      next: data => {
        this.companies = data.map(company => ({
          label: company.companyName,
          value: company.id
        }));
      }
    });
    this.subscriptions.add(subscription);
  }
}
